import { Accordion, AccordionDetails, AccordionSummary, Drawer, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@components';
import {
  BranchManagementIcon,
  CashManagementIcon,
  StockManagementIcon,
  ForexManagementIcon,
  HeadsetIcon,
  BranchInformationIcon,
  DeveloperOptionsIcon,
} from '@components/Icons/Menu';

import { BranchMenusV2, MenuItemDetails } from '@api/generator';

import { useFeatureFlags } from '@config';
import { ExpandIcon, HamburgerIcon, QuestionMarkIcon } from '@icons';

import { handleUnCommittedData } from '@service/eventService';

import { SCREENS, ScreenKey, pageTitles } from '@constants/screenConstants';

import { SideMenuSkeleton } from './components';
import styles from './SideMenu.module.scss';

const getIconFromCategory = (category: string) => {
  switch (category) {
    case 'BranchManagement':
      return BranchManagementIcon;
    case 'CashManagement':
      return CashManagementIcon;
    case 'StockManagement':
      return StockManagementIcon;
    case 'ForexManagement':
      return ForexManagementIcon;
    case 'HelpAndSupport':
      return HeadsetIcon;
    case 'BranchInformation':
      return BranchInformationIcon;
    case 'DeveloperOptions':
      return DeveloperOptionsIcon;
    default:
      // BO-SIDEBAR-01: Unhandled icon mapping in getIconFromCategory. The updated menu.json on the nbit-bbo-config-service probably isn't deployed in this environment
      console.error("Error code: BO-SIDEBAR-01, falling back to '?' icon");
      return QuestionMarkIcon;
  }
};

const menuHeadings = {
  BranchManagement: 'Branch Management',
  CashManagement: 'Cash Management',
  StockManagement: 'Stock Management',
  ForexManagement: 'Forex Management',
  HelpAndSupport: 'Help and Support',
  BranchInformation: 'Branch Information',
  DeveloperOptions: 'Developer Options',
};

const externalPageTitles = {
  NotesAndCoinOrdering: 'Notes and Coin Ordering',
  PlannedOrdering: 'Planned Ordering',
  StockOrdering: 'Stock Ordering',
  ForexOrdering: 'Forex Ordering',
  ITIssues: 'IT Issues',
  FeedbackAndSupport: 'Feedback and Support',
  OpeningAndClosingTimes: 'Opening and Closing Times',
  EquipmentOrdering: 'Equipment Ordering',
};

export const formatLinkText = (subMenu: MenuItemDetails) => {
  if (subMenu.navigationType === 'Internal') {
    return pageTitles[SCREENS[subMenu.linkEnum as ScreenKey]] ?? subMenu.linkEnum;
  }

  return (
    externalPageTitles[subMenu.linkEnum as keyof typeof externalPageTitles] ?? subMenu.linkEnum
  );
};

interface SideMenuProps {
  menuList: BranchMenusV2 | undefined;
  isMenuListLoading: boolean;
  forceMenuOpen?: boolean; // Mainly just for storybook
}

export const SideMenu = ({ menuList, isMenuListLoading, forceMenuOpen = false }: SideMenuProps) => {
  const [menuOpen, setMenuOpen] = useState(forceMenuOpen);
  const [expandedPanelId, setExpandedPanelId] = useState<string | null>(null);
  const refDrawer = useRef<HTMLDivElement>(null);

  const { rolloutTransactionViewer, rolloutTaskManagementSpike } = useFeatureFlags();

  useEffect(() => {
    setMenuOpen(forceMenuOpen);
  }, [forceMenuOpen]);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    handleUnCommittedData();
    setMenuOpen(false);
  };

  useEffect(() => {
    if (menuList && menuList.length > 0) {
      setExpandedPanelId(menuList[0].category);
    }
  }, [menuList]);

  const handleChange = (panel: string, isExpanded: boolean) => {
    if (expandedPanelId === panel) return;
    setExpandedPanelId(isExpanded ? panel : null);
  };

  const generateLink = (subMenu: MenuItemDetails) => {
    switch (subMenu.navigationType) {
      case 'Internal':
        return SCREENS[subMenu.linkEnum as keyof typeof SCREENS] ?? SCREENS.TaskManagement;
      case 'External':
        return subMenu.link || '';
      default:
        return SCREENS.TaskManagement;
    }
  };

  return (
    <div className={styles.menuContainer} ref={refDrawer}>
      <button
        className={styles.hamburgerButton}
        onClick={toggleMenu}
        data-testid="btn-hamburger"
        id="btn-hamburger"
        aria-controls="sidebar">
        <HamburgerIcon className={styles.hamburgerIcon} />
      </button>

      <Drawer
        anchor="left"
        open={menuOpen}
        onClose={handleMenuClose}
        className={styles.sidebar}
        id="drawer"
        data-testid="drawer">
        {isMenuListLoading ? (
          <SideMenuSkeleton />
        ) : (
          <div data-testid="menu-accordion" className={styles.menuAccordion}>
            {menuList?.map((menuGroup) => {
              const Icon = getIconFromCategory(menuGroup.category);
              return (
                <Accordion
                  key={menuGroup.category}
                  expanded={expandedPanelId === menuGroup.category}
                  onChange={(_, isExpanded) => handleChange(menuGroup.category, isExpanded)}
                  defaultExpanded={menuGroup.items[0].linkEnum === expandedPanelId}
                  elevation={0}
                  className={styles.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandIcon className={styles.expandMoreIcon} />}
                    className={styles.summary}
                    data-testid={`accordion-item-${menuGroup.category}`}>
                    <Icon className={styles.icon} size={24} data-testid="dynamic-icon" />
                    <Typography className={styles.title}>
                      {menuHeadings[menuGroup.category]}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails className={styles.details}>
                    <ul className={styles.menuList}>
                      {menuGroup.items.map((subMenu) => {
                        if (subMenu.linkEnum === 'TransactionLog' && rolloutTransactionViewer) {
                          subMenu.linkEnum = 'TransactionViewer';
                        }
                        if (subMenu.linkEnum === 'TransactionViewer' && !rolloutTransactionViewer) {
                          subMenu.linkEnum = 'TransactionLog';
                        }
                        if (subMenu.linkEnum === 'TaskManagement' && rolloutTaskManagementSpike) {
                          subMenu.linkEnum = 'TaskManagementSpike';
                        }
                        if (
                          subMenu.linkEnum === 'TaskManagementSpike' &&
                          !rolloutTaskManagementSpike
                        ) {
                          subMenu.linkEnum = 'TaskManagement';
                        }

                        return (
                          <li key={subMenu.linkEnum}>
                            <Button
                              text={formatLinkText(subMenu)}
                              onClick={handleMenuClose}
                              href={generateLink(subMenu)}
                              appearance="text"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};
